import { apiGet, apiPatch, getAuthRoles } from "./api";

export type PermissionKey =
  | "nav_inbox"
  | "nav_dashboard"
  | "nav_campaigns"
  | "nav_contacts"
  | "contacts_manage"
  | "nav_internal_chat"
  | "nav_schedule"
  | "nav_active_conversations"
  | "nav_chatbot_flow_builder"
  | "inbox_view_all"
  | "inbox_delete"
  | "settings_account"
  | "settings_agents"
  | "settings_groups"
  | "settings_inboxes"
  | "settings_tags"
  | "settings_custom_fields"
  | "settings_automation"
  | "settings_macros"
  | "settings_quick_replies"
  | "settings_integrations"
  | "settings_telephony"
  | "settings_audit"
  | "settings_sales_payments"
  | "settings_chatbot"
  | "settings_functions"
  | "settings_api"
  | "settings_sla"
  | "settings_csat";

export type RoleDefinition = {
  id: string;
  name: string;
  description: string;
  permissions: Record<PermissionKey, boolean>;
};

const STORAGE_KEY = "omni:customRoles";
const ACTIVE_ROLE_KEY = "omni:activeRoleId";
export const ROLES_EVENT = "omni:roles-updated";

const allPermissions: PermissionKey[] = [
  "nav_inbox",
  "nav_dashboard",
  "nav_campaigns",
  "nav_contacts",
  "contacts_manage",
  "nav_internal_chat",
  "nav_schedule",
  "nav_active_conversations",
  "nav_chatbot_flow_builder",
  "inbox_view_all",
  "inbox_delete",
  "settings_account",
  "settings_agents",
  "settings_groups",
  "settings_inboxes",
  "settings_tags",
  "settings_custom_fields",
  "settings_automation",
  "settings_macros",
  "settings_quick_replies",
  "settings_integrations",
  "settings_telephony",
  "settings_audit",
  "settings_sales_payments",
  "settings_chatbot",
  "settings_functions",
  "settings_api",
  "settings_sla",
  "settings_csat",
];

const buildPermissionMap = (enabled: PermissionKey[]) =>
  allPermissions.reduce((acc, key) => {
    acc[key] = enabled.includes(key);
    return acc;
  }, {} as Record<PermissionKey, boolean>);

export const defaultRoles: RoleDefinition[] = [
  {
    id: "admin",
    name: "Administrador",
    description: "Acesso total às configurações e dashboards.",
    permissions: buildPermissionMap(allPermissions),
  },
  {
    id: "supervisor",
    name: "Supervisor",
    description: "Gestão da operação, sem usuários, cobrança e integrações.",
    permissions: buildPermissionMap(
      allPermissions.filter(
        (key) =>
          !["settings_agents", "settings_integrations", "settings_sales_payments", "settings_functions", "settings_api"].includes(key)
      )
    ),
  },
  {
    id: "agent",
    name: "Atendente",
    description: "Perfil operacional para atendimento e suporte.",
    permissions: buildPermissionMap([
      "nav_inbox",
      "nav_contacts",
      "nav_internal_chat",
      "nav_schedule",
      "settings_account",
      "settings_quick_replies",
    ]),
  },
];

export const loadRoles = () => {
  if (typeof window === "undefined") return defaultRoles;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultRoles;
    const parsed = JSON.parse(raw) as RoleDefinition[];
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultRoles;
    return parsed;
  } catch {
    return defaultRoles;
  }
};

const roleIdFromToken = () => {
  const roles = getAuthRoles();
  if (roles.includes("TENANT_ADMIN") || roles.includes("SUPERADMIN")) return "admin";
  return roles.includes("SUPERVISOR") ? "supervisor" : "agent";
};

export const loadActiveRoleId = () => {
  if (typeof window === "undefined") return "agent";
  return window.localStorage.getItem(ACTIVE_ROLE_KEY) ?? roleIdFromToken();
};

// As funções ficam no servidor, que é quem aplica as permissões. O localStorage é só a cópia usada
// para desenhar a tela sem esperar a rede.
export const syncRolesFromServer = async () => {
  const data = await apiGet<{ roles: RoleDefinition[]; activeRoleId: string }>("/roles");
  if (!data?.roles?.length) return defaultRoles;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data.roles));
  window.localStorage.setItem(ACTIVE_ROLE_KEY, data.activeRoleId);
  window.dispatchEvent(new Event(ROLES_EVENT));
  return data.roles;
};

export const saveRolesToServer = async (roles: RoleDefinition[]) => {
  await apiPatch("/roles", { roles });
  return syncRolesFromServer();
};


// Sem a função procurada, fica com a de menor acesso, nunca com a de administrador.
export const getRoleById = (roles: RoleDefinition[], roleId: string) =>
  roles.find((role) => role.id === roleId) ?? roles.find((role) => role.id === "agent") ?? roles[roles.length - 1];
